import { generatePath } from 'react-router-dom';
import { segments } from 'routes/segments';
import { absolute, join } from 'routes/utils';

/**
 * URL pieces live in three places:
 *   segments   – the strings themselves ("game-data")
 *   routePaths – <Route path> patterns for individual routers ("/list/:id")
 *   paths      – absolute URLS for navigate() / <Link> ("/pitches/list/abc")
 *
 * To add a new subsection (e.g. /pitches/foo/:fooID):
 *   1. segments.ts            – { foo: 'foo' }  (skip if the word already exists)
 *   2. this file              – { routePaths.pitches.foo: '/foo/:fooID' }
 *   3. that section's router  – <Route path={routePaths.pitches.foo} />
 *   4. SectionsHelper         – SubSectionName + entry (invisible if it shouldn't show in the sidebar nav)
 */

// ── 1) Route patterns for individual routers ────────────────────────
export const routePaths = {
  // /pitches/...
  pitches: {
    lists: `/${segments.lists}`,
    library: `/${segments.library}`,
    design: `/${segments.design}/:listId?`, // optional list id seeds the new pitch's parent list
    upload: `/${segments.upload}`,
    gameData: `/${segments.gameData}`,
    list: `/${segments.list}/:id`,
  },

  // /pitches/game-data/...
  gameData: {
    index: '/',
    pitcher: `/${segments.pitchers}/:playerPk`,
    hitter: `/${segments.hitters}/:playerPk`,
    game: `/${segments.games}/:gamePk`,
  },

  // /hitter-library/...
  hitterLibrary: {
    hitters: `/${segments.hitters}`,
    profile: `/${segments.hitterProfile}/:hitterID`,
    session: `/${segments.hitterSession}/:session/:hitterID`,
  },

  // /analytics/...
  analytics: {
    hittingReports: `/${segments.hittingReports}`,
    sessionHistory: `/${segments.sessionHistory}`,
    hitterSession: `/${segments.hitterSession}/:sessionID/:hitterID?`,
  },

  // /admin-portal/...
  admin: {
    users: `/${segments.users}`,
    machines: `/${segments.machines}`,
    organizations: `/${segments.organizations}`,
    ballTypes: `/${segments.ballTypes}`,
    machineModels: `/${segments.machineModels}`,
    errorTypes: `/${segments.errorTypes}`,
    staticVideos: `/${segments.staticVideos}`,
    apiKeys: `/${segments.apiKeys}`,
    serverErrors: `/${segments.serverErrors}`,
    serverControls: `/${segments.serverControls}`,
  },

  // /platform-console/...
  platformConsole: {
    featureFlags: `/${segments.featureFlags}`,
    jobs: `/${segments.jobs}`,
  },
} as const;

// ── 2) Absolute URLs for navigate() / <Link> ────────────────────────
// Section root + routePaths (+ route params).
const pitchesRoot = `/${segments.pitches}`;
const hitterLibraryRoot = `/${segments.hitterLibrary}`;
const analyticsRoot = `/${segments.analytics}`;
const adminRoot = `/${segments.admin}`;
const platformConsoleRoot = `/${segments.platformConsole}`;

const adminAbsolutes = Object.fromEntries(
  Object.entries(routePaths.admin).map(([key, pattern]) => [
    key,
    absolute(adminRoot, pattern),
  ])
) as { [K in keyof typeof routePaths.admin]: string };

const platformConsoleAbsolutes = Object.fromEntries(
  Object.entries(routePaths.platformConsole).map(([key, pattern]) => [
    key,
    absolute(platformConsoleRoot, pattern),
  ])
) as { [K in keyof typeof routePaths.platformConsole]: string };

export const paths = {
  home: absolute(pitchesRoot, routePaths.pitches.lists),

  gameInProgress: `/${segments.gameInProgress}`,
  unavailable: `/${segments.unavailable}`,
  notFound: `/${segments.notFound}`,

  pitches: {
    root: pitchesRoot,
    lists: absolute(pitchesRoot, routePaths.pitches.lists),
    library: absolute(pitchesRoot, routePaths.pitches.library),
    upload: absolute(pitchesRoot, routePaths.pitches.upload),
    design: (listId?: string) =>
      absolute(pitchesRoot, routePaths.pitches.design, { listId }),
    list: (id: string) =>
      absolute(pitchesRoot, routePaths.pitches.list, { id }),
    gameData: {
      root: absolute(pitchesRoot, routePaths.pitches.gameData),
      pitcher: (playerPk: string) =>
        join(
          absolute(pitchesRoot, routePaths.pitches.gameData),
          generatePath(routePaths.gameData.pitcher, { playerPk })
        ),
      hitter: (playerPk: string) =>
        join(
          absolute(pitchesRoot, routePaths.pitches.gameData),
          generatePath(routePaths.gameData.hitter, { playerPk })
        ),
      game: (gamePk: string) =>
        join(
          absolute(pitchesRoot, routePaths.pitches.gameData),
          generatePath(routePaths.gameData.game, { gamePk })
        ),
    },
  },

  hitterLibrary: {
    root: hitterLibraryRoot,
    hitters: absolute(hitterLibraryRoot, routePaths.hitterLibrary.hitters),
    profile: (hitterID: string) =>
      absolute(hitterLibraryRoot, routePaths.hitterLibrary.profile, {
        hitterID,
      }),
    session: (session: string, hitterID: string) =>
      absolute(hitterLibraryRoot, routePaths.hitterLibrary.session, {
        session,
        hitterID,
      }),
  },

  videoLibrary: `/${segments.videoLibrary}`,

  analytics: {
    root: analyticsRoot,
    hittingReports: absolute(
      analyticsRoot,
      routePaths.analytics.hittingReports
    ),
    sessionHistory: absolute(
      analyticsRoot,
      routePaths.analytics.sessionHistory
    ),
    hitterSession: (sessionID: string, hitterID?: string) =>
      absolute(analyticsRoot, routePaths.analytics.hitterSession, {
        sessionID,
        hitterID,
      }),
  },

  admin: {
    root: adminRoot,
    default: adminAbsolutes.machines,
    ...adminAbsolutes,
  },

  platformConsole: {
    root: platformConsoleRoot,
    default: platformConsoleAbsolutes.featureFlags,
    ...platformConsoleAbsolutes,
    // create/detail live on their own absolute routes (the console mounts under
    // "*", so <Route path> needs the full pathname). The detail pattern keeps
    // the ":flagId" literal for <Route>, while featureFlagDetail() fills it in
    // for navigate(). Built via join (not absolute) so generatePath never runs
    // against an unfilled param.
    featureFlagCreate: join(
      platformConsoleRoot,
      `/${segments.featureFlags}/${segments.new}`
    ),
    featureFlagDetailPattern: join(
      platformConsoleRoot,
      `/${segments.featureFlags}/:flagId`
    ),
    featureFlagDetail: (flagId: string) =>
      absolute(platformConsoleRoot, `/${segments.featureFlags}/:flagId`, {
        flagId,
      }),
  },

  featureDemo: `/${segments.featureDemo}`,

  login: {
    auth0: `/${segments.auth0}`,
    legacy: `/${segments.legacy}`,
  },
};
